import { useState, useEffect } from "react";
import { formatPhoneInput } from "../lib/format";
import { Conversations } from "./Conversations";
import { MiniChat } from "./MiniChat";
import { useBackClose } from "../lib/navStack";
import { useComms } from "../lib/commsReady";
import { useIsMobile } from "../lib/useIsMobile";

// ════════════════════════════════════════════════════════════════════
//  Leads — a PIPELINE + PRIORITY board for the operator. The job of this
//  view is to convert operator ATTENTION → ACTION: follow up and close
//  leads before they go cold. Same design caliber as the conversion
//  signup form, aimed at the OPERATOR instead of the customer.
//
//  Psychology in play:
//   • Three status TRAYS (New → Contacted → Converted) read left-to-right
//     as pipeline momentum, with column counts as the scoreboard.
//   • Out-of-area leads get their own tray — a waitlist / expansion track,
//     NOT convertible until the zip is serviced.
//   • In-column ordering surfaces what needs action: open trays put the
//     COLDEST (oldest, most at-risk) leads first — loss aversion — while
//     newest sit just behind. Converted shows newest-won first.
//   • Aging leads visually "go cold": the card body fades and an amber
//     "going cold" age chip appears once a lead has sat too long.
//   • One-tap Call / Text / Convert stay BRIGHT even as the body fades —
//     the action never dims, only the urgency around it grows.
//
//  PURELY PRESENTATIONAL — receives leads + handlers from App. The CRM
//  reads/writes the leads table via RLS (scoped to the caller's org).
// ════════════════════════════════════════════════════════════════════

const fullName = (l) => `${l.first_name || ""} ${l.last_name || ""}`.trim();

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || "").join("").toUpperCase();

// Whole-day gap between the lead's created_at and now (floor of the ms
// difference / one day). Urgency thresholds: 3d = getting warm, 7d = cold.
const WARM_DAYS = 3;
const COLD_DAYS = 7;
const ageDaysOf = (s) => {
  if (!s) return 0;
  const t = new Date(s).getTime();
  if (isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
};
// "Today", "1d", "2d" … the short age chip text.
const ageLabel = (days) => (days <= 0 ? "Today" : `${days}d`);

// Human label for a cleanup cadence.
const FREQ_LABEL = (f) => ({ weekly: "Weekly", biweekly: "Bi-weekly", "twice-weekly": "Twice/week", twice_weekly: "Twice/week", monthly: "Monthly" }[f] || f);
// Shared badge style for the "what they want" row.
const planTag = { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 8, background: "var(--accent-weak)", color: "var(--accent)", whiteSpace: "nowrap" };
// True when the lead carries any of the service-intent fields worth surfacing.
const hasIntent = (l) => !!(l.frequency || l.dogs || l.deo_enabled || l.quoted_monthly != null);

// Where the lead came from, in plain English.
const SOURCE_LABEL = (s) => ({ signup_form: "Signup form", embed: "Website widget", hosted_site: "Operator website", website: "Website" }[s] || (s ? String(s).replace(/_/g, " ") : "—"));
const fmtDateTime = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };
const statusBadge = (l) =>
  l.status === "converted" ? { t: "Converted", c: "var(--ok)", bg: "rgba(26,138,82,.12)" }
  : l.status === "contacted" ? { t: "Contacted", c: "#9A5B00", bg: "rgba(178,107,0,.12)" }
  : l.status === "dismissed" ? { t: "Dismissed", c: "var(--text2)", bg: "rgba(0,0,0,.06)" }
  : { t: "New", c: "var(--accent)", bg: "var(--accent-weak)" };

// One label/value row — renders nothing when the value is empty (keeps it clean).
const AV_COLORS = ["#6C5CE7", "#2BA3A3", "#C4694E", "#5A6AD0", "#3B8E5A", "#B0568E", "#C19A2E", "#4C8DBD"];
const avColor = (s = "") => AV_COLORS[Math.abs([...s].reduce((a, ch) => a + ch.charCodeAt(0), 0)) % AV_COLORS.length];

function Field({ label, children }) {
  const isMobile = useIsMobile();
  if (children == null || children === "") return null;
  // Phones: label stacks over the value (a 104px label column left ~180px for addresses / URLs).
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "104px 1fr", gap: isMobile ? 3 : 10, alignItems: "baseline", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
      <div style={{ fontSize: 13.5, color: "var(--text)", minWidth: 0, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 2 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Lead detail modal ───────────────────────────────────────────────
function LeadModal({ lead, onClose, onUpdateStatus, onConvertLead, onText, showToast }) {
  const [tab, setTab] = useState("details");
  const leadId = lead && lead.id;
  useEffect(() => { setTab("details"); }, [leadId]);
  const { requireTexting } = useComms();
  const isMobile = useIsMobile();
  if (!lead) return null;
  const name       = fullName(lead);
  const callLead = () => { if (!requireTexting()) return; if (!lead.phone) return; if (window.scoopDial) window.scoopDial(lead.phone, name || lead.phone); else window.location.href = `tel:${lead.phone}`; };
  const goMessages = () => { if (requireTexting()) setTab("messages"); };
  const outOfArea  = lead.kind === "out_of_area";
  const canConvert = !outOfArea && (lead.status === "new" || lead.status === "contacted");
  const sb         = statusBadge(lead);
  const area       = outOfArea ? { t: "Out of area", c: "var(--danger)", bg: "rgba(215,50,43,.10)" } : { t: "In area", c: "var(--ok)", bg: "rgba(26,138,82,.10)" };
  const days       = ageDaysOf(lead.created_at);
  const addr       = [lead.address, [lead.city, lead.state].filter(Boolean).join(", "), lead.zip].filter(Boolean).join(" · ");
  const quoted     = [lead.quoted_per_visit != null ? `$${Number(lead.quoted_per_visit).toFixed(2)}/visit` : null,
                      lead.quoted_monthly  != null ? `$${Number(lead.quoted_monthly).toFixed(2)}/mo` : null].filter(Boolean).join(" · ");
  const act = (fn) => { fn(); onClose(); }; // status-changing actions move the lead → close
  const tag = { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 8 };
  // Phones: a full-width bottom sheet (85dvh, own scroller, safe-area footer) instead of the
  // desktop right-hand drawer; every footer action becomes a full-size button.
  const sheet = isMobile
    ? { width: "100%", maxWidth: "100%", height: "auto", maxHeight: "85dvh", minHeight: 0, borderRadius: "var(--r-lg) var(--r-lg) 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { width: 408, maxWidth: "94vw", height: "100%", maxHeight: "100%", borderRadius: 0, display: "flex", flexDirection: "column" };
  const padM = isMobile ? { padding: "14px 16px" } : undefined;
  const mb = isMobile ? { flex: "1 1 45%", minHeight: 44, fontSize: 14 } : undefined;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} style={{ justifyContent: isMobile ? "center" : "flex-end", alignItems: isMobile ? "flex-end" : "stretch", padding: 0 }}>
      <div className="modal" style={sheet}>
        <div className="modal-header" style={padM}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
            <span style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: "#fff", background: lead.status === "converted" ? "var(--ok)" : avColor(name) }}>{initials(name) || "•"}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {name || (lead.phone ? formatPhoneInput(lead.phone) : "New lead")}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ ...tag, background: sb.bg, color: sb.c }}>{sb.t}</span>
                <span style={{ ...tag, background: area.bg, color: area.c }}>{area.t}</span>
                <span style={{ fontSize: 11.5, color: "var(--text3)" }}>Added {days <= 0 ? "today" : `${days}d ago`}</span>
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close" style={isMobile ? { width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: -8 } : undefined}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 18, padding: "0 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          {[["details", "Details"], ["messages", "💬 Messages"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => v === "messages" ? goMessages() : setTab(v)}
              style={{ border: "none", background: "transparent", padding: "10px 0", fontSize: 13, fontWeight: tab === v ? 600 : 500, cursor: "pointer", color: tab === v ? "var(--accent)" : "var(--text2)", borderBottom: tab === v ? "2px solid var(--accent)" : "2px solid transparent", marginBottom: -1,
                ...(isMobile ? { flex: 1, minHeight: 44, fontSize: 14 } : null) }}>{l}</button>
          ))}
        </div>

        <div className="modal-body" style={{ ...(padM || {}), paddingTop: 6, flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
          {tab === "messages" ? (
            <Section title="Quick message">
              <MiniChat scope="lead" contactId={lead.id} phone={lead.phone} showToast={showToast} />
              {onText && <button className={`btn btn-ghost ${isMobile ? "btn-sm" : "btn-xs"}`} style={{ marginTop: 10 }} onClick={() => onText(lead)}>Open full conversation →</button>}
            </Section>
          ) : (<>
          <Section title="Contact">
            <Field label="Phone">{lead.phone ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {formatPhoneInput(lead.phone)}
                <button className={`btn btn-ghost ${isMobile ? "btn-sm" : "btn-xs"}`} onClick={callLead}>📞 Call</button>
                <button className={`btn btn-ghost ${isMobile ? "btn-sm" : "btn-xs"}`} onClick={goMessages}>💬 Text</button>
              </span>) : null}</Field>
            <Field label="Email">{lead.email ? <a href={`mailto:${lead.email}`} style={{ color: "var(--accent)" }}>{lead.email}</a> : null}</Field>
            <Field label="Address">{addr || null}</Field>
          </Section>

          {(hasIntent(lead) || lead.yard_size) && (
            <Section title="What they want">
              <Field label="Service">{lead.deo_enabled ? "Cleanup + deodorizer" : (lead.frequency || lead.dogs ? "Cleanup" : null)}</Field>
              <Field label="Frequency">{lead.frequency ? FREQ_LABEL(lead.frequency) : null}</Field>
              <Field label="Dogs">{lead.dogs ? `${lead.dogs} dog${lead.dogs > 1 ? "s" : ""}` : null}</Field>
              <Field label="Deodorizer">{lead.deo_enabled ? `Yes${lead.deo_frequency ? ` · ${FREQ_LABEL(lead.deo_frequency)}` : ""}` : null}</Field>
              <Field label="Yard size">{lead.yard_size || null}</Field>
              <Field label="Quoted">{quoted || null}</Field>
            </Section>
          )}

          <Section title="Details">
            <Field label="Source">{SOURCE_LABEL(lead.source)}</Field>
            <Field label="Added">{fmtDateTime(lead.created_at)}</Field>
            <Field label="Notes">{lead.notes || null}</Field>
            <Field label="Came from">{lead.page_url ? <a href={lead.page_url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>{lead.page_url}</a> : null}</Field>
          </Section>
          </>)}
        </div>

        <div className="modal-footer" style={isMobile
          ? { flexDirection: "column-reverse", alignItems: "stretch", gap: 8, padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))", flexShrink: 0 }
          : { justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8 }}>
            {(lead.status === "new" || lead.status === "contacted") && (
              <button className="btn btn-ghost btn-sm" style={{ color: "var(--text2)", ...(isMobile ? { flex: 1, minHeight: 40 } : null) }} onClick={() => act(() => onUpdateStatus(lead.id, "dismissed"))}>Dismiss</button>
            )}
            {lead.status === "dismissed" && (
              <button className="btn btn-ghost btn-sm" style={isMobile ? { flex: 1, minHeight: 40 } : undefined} onClick={() => act(() => onUpdateStatus(lead.id, "new"))}>Reopen</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {lead.phone && <button className="btn btn-sm" onClick={callLead} style={{ background: "var(--accent)", color: "#fff", border: "none", ...mb }}>📞 Call</button>}
            {lead.phone && <button className="btn btn-ghost btn-sm" onClick={goMessages} style={mb}>💬 Text</button>}
            {lead.status === "new" && <button className="btn btn-ghost btn-sm" onClick={() => act(() => onUpdateStatus(lead.id, "contacted"))} style={mb}>Mark contacted</button>}
            {canConvert && <button className="btn btn-gold btn-sm" onClick={() => act(() => onConvertLead(lead))} style={mb}>Convert to client</button>}
            {lead.status === "converted" && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ok)", alignSelf: "center" }}>✓ Converted</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── A single lead card ──────────────────────────────────────────────
function LeadCard({ lead, onUpdateStatus, onConvertLead, onSelect, onText }) {
  const { requireTexting } = useComms();
  const isMobile  = useIsMobile();
  // Phones: btn-xs (~19px) is too small next to a tappable card body — a near-miss opens the
  // drawer instead of calling. Promote every action to a 36px+ target.
  const bcls      = isMobile ? "btn-sm" : "btn-xs";
  const bx        = isMobile ? { minHeight: 36, padding: "8px 12px", fontSize: 13 } : undefined;
  const name      = fullName(lead);
  const callLead  = () => { if (!requireTexting()) return; if (!lead.phone) return; if (window.scoopDial) window.scoopDial(lead.phone, name || lead.phone); else window.location.href = `tel:${lead.phone}`; };
  const days      = ageDaysOf(lead.created_at);
  const isCold     = days >= COLD_DAYS;
  const isWarm     = days >= WARM_DAYS && !isCold;
  const outOfArea  = lead.kind === "out_of_area";
  const converted  = lead.status === "converted";
  const dismissed  = lead.status === "dismissed";
  const canConvert = !outOfArea && (lead.status === "new" || lead.status === "contacted");
  const mono       = initials(name) || "•";

  // Loss aversion: the body (identity + meta) fades as the lead goes cold;
  // the action row below stays at full opacity so doing something is easy.
  const bodyOpacity = converted ? 0.62 : dismissed ? 0.5 : isCold ? 0.64 : 1;

  // Age chip — amber once cold, neutral otherwise. Converted shows a calm
  // green "won" instead of an age.
  const ageChip = converted
    ? { text: "Won", color: "var(--ok)", bg: "rgba(26,138,82,.12)" }
    : isCold
      ? { text: `${ageLabel(days)} · cold`, color: "#9A5B00", bg: "rgba(178,107,0,.14)" }
      : isWarm
        ? { text: ageLabel(days), color: "#9A5B00", bg: "rgba(178,107,0,.10)" }
        : { text: ageLabel(days), color: "var(--text2)", bg: "rgba(0,0,0,.05)" };

  const areaTag = outOfArea
    ? { text: "Out of area", color: "var(--danger)", bg: "rgba(215,50,43,.10)" }
    : { text: "In area", color: "var(--ok)", bg: "rgba(26,138,82,.10)" };

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--r)",
        padding: "11px 12px",
        boxShadow: "var(--shadow-sm)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* ── Body (fades when cold) — click to open the detail modal ── */}
      <div
        role="button" tabIndex={0}
        onClick={() => onSelect && onSelect(lead)}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onSelect) { e.preventDefault(); onSelect(lead); } }}
        title="View lead details"
        aria-label={`View details for ${name || lead.phone || "lead"}`}
        style={{ display: "flex", gap: 10, alignItems: "flex-start", opacity: bodyOpacity, transition: "opacity .2s var(--ease)", cursor: "pointer" }}>
        <span style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#fff", background: converted ? "var(--ok)" : avColor(name) }}>
          {mono}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Primary line: name, or the phone if no name yet */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {name || (lead.phone ? formatPhoneInput(lead.phone) : "New lead")}
            </span>
            <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: ageChip.bg, color: ageChip.color }}>
              {ageChip.text}
            </span>
          </div>

          {/* Secondary line: area tag + zip */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: areaTag.bg, color: areaTag.color }}>
              {areaTag.text}
            </span>
            {lead.zip && <span style={{ fontSize: 12, color: "var(--text2)" }}>{lead.zip}</span>}
            {lead.city && <span style={{ fontSize: 12, color: "var(--text3)" }}>{lead.city}{lead.state ? `, ${lead.state}` : ""}</span>}
          </div>

          {/* What they're looking for — frequency, dogs, deodorizer, and the
              price they were quoted at reveal. Scannable so the operator sees
              each lead's intent at a glance; empty for a not-yet-quoted lead. */}
          {hasIntent(lead) && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
              {lead.frequency && <span style={planTag}>{FREQ_LABEL(lead.frequency)}</span>}
              {lead.dogs ? <span style={planTag}>🐕 {lead.dogs}</span> : null}
              {lead.deo_enabled && (
                <span style={{ ...planTag, background: "rgba(178,107,0,.12)", color: "#9A5B00" }}>
                  💨 Deo{lead.deo_frequency ? ` · ${lead.deo_frequency}` : ""}
                </span>
              )}
              {lead.quoted_monthly != null && (
                <span style={{ ...planTag, background: "rgba(26,138,82,.12)", color: "var(--ok)" }}>
                  ${Number(lead.quoted_monthly).toFixed(0)}/mo quoted
                </span>
              )}
            </div>
          )}
        </div>

        {/* Touch has no hover/tooltip: a visible chevron tells the operator the body opens details. */}
        {isMobile && (
          <span aria-hidden="true" style={{ flexShrink: 0, alignSelf: "center", fontSize: 20, lineHeight: 1, color: "var(--text3)", padding: "0 2px" }}>›</span>
        )}
      </div>

      {/* ── Actions (stay bright) ── */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 6, flexWrap: "wrap" }}>
        {lead.phone && (
          <>
            <button className={`btn btn-ghost ${bcls}`} style={bx} onClick={callLead}>📞 Call</button>
            <button className={`btn btn-ghost ${bcls}`} style={bx} onClick={() => onText && onText(lead)}>💬 Text</button>
          </>
        )}

        {canConvert && (
          <button className={`btn btn-gold ${bcls}`} style={bx} onClick={() => onConvertLead(lead)} title="Convert to client">Convert</button>
        )}
        {lead.status === "new" && (
          <button className={`btn btn-ghost ${bcls}`} style={bx} onClick={() => onUpdateStatus(lead.id, "contacted")} title="Mark contacted">Mark contacted</button>
        )}
        {(lead.status === "new" || lead.status === "contacted") && (
          <button className={`btn btn-ghost ${bcls}`} onClick={() => onUpdateStatus(lead.id, "dismissed")} title="Dismiss" style={{ marginLeft: "auto", color: "var(--text2)", ...bx }}>Dismiss</button>
        )}
        {dismissed && (
          <button className={`btn btn-ghost ${bcls}`} style={bx} onClick={() => onUpdateStatus(lead.id, "new")} title="Reopen">Reopen</button>
        )}
        {converted && (
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ok)", display: "inline-flex", alignItems: "center", gap: 5 }}>
            ✓ Converted to client
          </span>
        )}
      </div>
    </div>
  );
}

// ── A status tray (column) ──────────────────────────────────────────
function Tray({ title, subtitle, accent, leads, coldCount, onUpdateStatus, onConvertLead, onSelect, onText, emptyMsg }) {
  // Desktop: the board is its own scroller, so a sticky header stays put inside it.
  // Phones: the DOCUMENT scrolls and the 60px topbar is sticky — a sticky tray header
  // would tuck under it, so it just flows.
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        background: "#FAFAFB",
        border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      {/* Sticky tray header — the scoreboard */}
      <div
        style={{
          position: isMobile ? "static" : "sticky",
          top: 0,
          zIndex: 2,
          background: "#FAFAFBE6",
          backdropFilter: "saturate(180%) blur(8px)",
          WebkitBackdropFilter: "saturate(180%) blur(8px)",
          borderBottom: "1px solid var(--border)",
          borderTopLeftRadius: "var(--r-lg)",
          borderTopRightRadius: "var(--r-lg)",
          padding: "12px 13px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent, flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{title}</span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "var(--text2)", background: "rgba(0,0,0,.05)", borderRadius: 9, padding: "1px 8px" }}>
            {leads.length}
          </span>
        </div>
        <div style={{ marginTop: 4, fontSize: 11, minHeight: 14, color: coldCount > 0 ? "#9A5B00" : "var(--text3)", fontWeight: coldCount > 0 ? 600 : 400 }}>
          {coldCount > 0 ? `${coldCount} going cold` : subtitle}
        </div>
      </div>

      {/* Tray body */}
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
        {leads.length === 0 && (
          <div style={{ padding: "18px 8px", textAlign: "center", fontSize: 12, color: "var(--text3)" }}>{emptyMsg}</div>
        )}
        {leads.map(l => (
          <LeadCard key={l.id} lead={l} onUpdateStatus={onUpdateStatus} onConvertLead={onConvertLead} onSelect={onSelect} onText={onText} />
        ))}
      </div>
    </div>
  );
}

export function Leads(props) {
  const { leads = [], onUpdateStatus, onConvertLead } = props;
  const [search, setSearch] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  const [selected, setSelected] = useState(null); // lead shown in the detail drawer
  const [view, setView] = useState("pipeline");   // pipeline | messages
  const [textOpenId, setTextOpenId] = useState(null);
  const [mobileTray, setMobileTray] = useState("new"); // phones show ONE tray at a time (new | contacted | converted | out)
  const { requireTexting } = useComms();
  const isMobile = useIsMobile();
  const openText = (l) => { if (!requireTexting()) return; if (!l || !l.phone) return; setSelected(null); setTextOpenId(l.id); setView("messages"); };
  useBackClose(!!selected, () => setSelected(null)); // Back closes the lead drawer, not the tab
  useBackClose(view === "messages", () => setView("pipeline")); // Back returns to the pipeline, not the prior tab

  const q = search.trim().toLowerCase();
  const matches = (l) => {
    if (!q) return true;
    const hay = [fullName(l), l.email, l.phone, l.zip, l.city, l.state].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };

  // Open-tray ordering: COLDEST (oldest) first to fight loss aversion, then
  // newest just behind. Converted reads newest-won first.
  const coldFirst = (a, b) => new Date(a.created_at) - new Date(b.created_at);
  const newestFirst = (a, b) => new Date(b.created_at) - new Date(a.created_at);

  const visible = leads.filter(matches);
  const dismissedAll = leads.filter(l => l.status === "dismissed");

  // In-area pipeline (out-of-area never enters New/Contacted/Converted here —
  // it lives in its own waitlist tray).
  const newLeads = visible.filter(l => l.status === "new" && l.kind !== "out_of_area").sort(coldFirst);
  const contacted = visible.filter(l => l.status === "contacted" && l.kind !== "out_of_area").sort(coldFirst);
  const converted = visible.filter(l => l.status === "converted").sort(newestFirst);
  const outOfArea = visible.filter(l => l.kind === "out_of_area" && l.status !== "converted" && l.status !== "dismissed").sort(newestFirst);
  const dismissed = visible.filter(l => l.status === "dismissed").sort(newestFirst);

  const coldCount = (arr) => arr.filter(l => ageDaysOf(l.created_at) >= COLD_DAYS).length;
  const totalCold = coldCount(newLeads) + coldCount(contacted);

  const open = leads.filter(l => l.status === "new" || l.status === "contacted").length;
  const _now = new Date();
  const convMonth = converted.filter(l => { const d = new Date(l.updated_at || l.created_at || 0); return d.getFullYear() === _now.getFullYear() && d.getMonth() === _now.getMonth(); }).length;

  // The four trays, in pipeline order. Desktop lays them out as columns; phones render a
  // segmented tray picker + the ONE selected tray (stacking all four put Converted and
  // Out-of-area below every New + Contacted card).
  const trayProps = { onSelect: setSelected, onText: openText, onUpdateStatus, onConvertLead };
  const trays = [
    { key: "new",       title: "New",         subtitle: "Reach out fast — speed wins",      accent: "var(--accent)", leads: newLeads,  coldCount: coldCount(newLeads),  emptyMsg: q ? "No matches" : "No new leads — nice and clear" },
    { key: "contacted", title: "Contacted",   subtitle: "Follow up to close",              accent: "#B26B00",       leads: contacted, coldCount: coldCount(contacted), emptyMsg: q ? "No matches" : "Nothing in follow-up yet" },
    { key: "converted", title: "Converted",   subtitle: "Closed and won",                  accent: "var(--ok)",     leads: converted, coldCount: 0,                    emptyMsg: q ? "No matches" : "Convert a lead to start the streak" },
    { key: "out",       title: "Out of area", subtitle: "Waitlist — expansion planning",   accent: "var(--text2)",  leads: outOfArea, coldCount: 0,                    emptyMsg: q ? "No matches" : "No out-of-area interest yet" },
  ];
  const shownTrays = isMobile ? trays.filter(t => t.key === mobileTray) : trays;

  // The page's one control style — the same quiet segmented pill the Pipeline/Messages
  // switch uses (design rule: one control style per page).
  const segWrap = { display: "inline-flex", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 9, padding: 2, gap: 2, flexShrink: 0 };
  const segBtn = (on) => ({ border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
    background: on ? "var(--surface)" : "transparent", color: on ? "var(--text)" : "var(--text2)", boxShadow: on ? "var(--shadow-sm)" : "none" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Row 1 — quiet stat line + the view-mode switch (distinct from the board) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--text2)" }}>
          {view === "messages" ? "Lead conversations" : (<>
            <strong style={{ color: "var(--text)", fontWeight: 600 }}>{open}</strong> open · <strong style={{ color: "var(--text)", fontWeight: 600 }}>{convMonth}</strong> converted this month
            {totalCold > 0 && <> · <span style={{ color: "#B26B00", fontWeight: 600 }}>{totalCold} going cold</span></>}
          </>)}
        </div>
        <div style={segWrap}>
          {[["pipeline", "Pipeline"], ["messages", "💬 Messages"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setView(v)}
              style={{ ...segBtn(view === v), ...(isMobile ? { minHeight: 36, padding: "6px 14px" } : null) }}>{l}</button>
          ))}
        </div>
      </div>

      {view === "messages" ? (
        <Conversations scope="lead" rows={leads} showToast={props.showToast} openId={textOpenId} />
      ) : (<>

      {/* Search + dismissed toggle */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, phone, email, or zip…"
          aria-label="Search leads"
          style={{ flex: 1, minWidth: isMobile ? 0 : 240 }}
        />
        <button
          className={`btn btn-ghost ${isMobile ? "btn-sm" : "btn-xs"}`}
          onClick={() => setShowDismissed(v => !v)}
          style={{ flexShrink: 0 }}
        >
          {showDismissed ? "Hide dismissed" : `Show dismissed${dismissedAll.length ? ` (${dismissedAll.length})` : ""}`}
        </button>
      </div>

      {/* The board — four trays wrapped in one card. Columns shrink to always
          fit the width (NO horizontal scrollbar); the board scrolls vertically. */}
      <div className="card" style={{ padding: 12 }}>
        {/* Phones: segmented tray picker (4 equal cells, label over count) above the one tray. */}
        {isMobile && (
          <div role="tablist" aria-label="Lead trays" style={{ ...segWrap, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", width: "100%", boxSizing: "border-box", marginBottom: 12 }}>
            {trays.map(t => {
              const on = t.key === mobileTray;
              return (
                <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => setMobileTray(t.key)}
                  style={{ ...segBtn(on), padding: "6px 4px", minHeight: 48, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: on ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{t.title}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: on ? "var(--text)" : "var(--text3)" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent, flexShrink: 0 }} />
                    {t.leads.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
            gap: 12,
            alignItems: "start",
            // Desktop: the board is its own vertical scroller under the sticky tray headers.
            // Phones: the DOCUMENT scrolls — a nested scroller double-scrolls / traps on iOS.
            overflowX: isMobile ? "clip" : "hidden",
            overflowY: isMobile ? "visible" : "auto",
            maxHeight: isMobile ? "none" : "calc(100vh - 240px)",
          }}
        >
          {shownTrays.map(t => (
            <Tray key={t.key} title={t.title} subtitle={t.subtitle} accent={t.accent} leads={t.leads} coldCount={t.coldCount} emptyMsg={t.emptyMsg} {...trayProps} />
          ))}
        </div>

        {/* Dismissed — hidden by default, opt-in below the board */}
        {showDismissed && (
          <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", marginBottom: 10 }}>
              Dismissed{dismissed.length ? ` · ${dismissed.length}` : ""}
            </div>
            {dismissed.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text3)" }}>{q ? "No matches" : "No dismissed leads"}</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))", gap: 10 }}>
                {dismissed.map(l => (
                  <LeadCard key={l.id} lead={l} onUpdateStatus={onUpdateStatus} onConvertLead={onConvertLead} onSelect={setSelected} onText={openText} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </>)}

      <LeadModal
        lead={selected}
        onClose={() => setSelected(null)}
        onUpdateStatus={onUpdateStatus}
        onConvertLead={onConvertLead}
        onText={openText}
        showToast={props.showToast}
      />
    </div>
  );
}
